import { cloneDeep } from 'lodash';
import { createJSONStorage, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { logger } from '@/lib/logger';

import { trackError } from '../../utils/errors';
import { syncTabs } from '../middleware/sync-tabs';
import {
  CrossChainTransaction,
  CurrentTransactionInfo,
  Transaction,
  TransactionsByAccount,
} from './types';
// utils
import {
  isTransactionCompletedSuccessfully,
  isTransactionCompletedWithFailure,
  isTransactionPending,
} from './utils';

export interface TransactionStore {
  recentTransaction: boolean;
  setRecentTransaction: (recentTransaction: boolean) => void;
  currentTransaction: CurrentTransactionInfo | null;
  transactions: TransactionsByAccount;
  addTransaction: (tx: Transaction, address: string, chainId: number) => void;
  updateTransaction: (
    txHash: string,
    address: string,
    chainId: number,
    updates: Partial<Transaction>,
  ) => void;
  updateCrossChainTransaction: (txHash: string, updates: Partial<CrossChainTransaction>) => void;
  completeTransactions: (receipt: Transaction[], address: string, chainId: number) => void;
  /**
   * Cross-tab idempotency claim for terminal analytics. Returns true if the caller
   * should emit, false if already emitted. Returns true when no record matches, so
   * a pruned transaction never drops its event. Matches by `hash` then `originalHash`.
   */
  claimTerminalAnalytics: (hash: string, address: string, chainId: number) => boolean;
}

let isRehydrating = false;

export function pruneTransactions(transactions: TransactionsByAccount) {
  const now = Date.now();
  const cutoff = now - 30 * 60 * 1000; // 30 minutes

  const cloned = cloneDeep(transactions) || {};

  for (const account in cloned) {
    for (const chainId in cloned[account]) {
      const nChainId = Number(chainId);

      if (Number.isNaN(nChainId)) {
        // this just assists to help typescript out
        continue;
      }
      //  I hate all this yelling at typescript but it's a necessary evil, it is unable to infer the type correctly
      cloned![account]![nChainId] = cloned![account]![nChainId]!.filter(
        (tx) => new Date(tx.timestamp).getTime() > cutoff,
      );
      if (cloned![account]![nChainId]!.length === 0) {
        delete cloned![account]![nChainId];
      }
    }
    if (Object.keys(cloned![account] || {}).length === 0) {
      delete cloned![account];
    }
  }
  return cloned || {};
}

function logStorageSizes() {
  const sizes = {} as Record<string, number>;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    sizes[key] = localStorage.getItem(key)?.length ?? 0;
  }
}

function customStorage() {
  const localStorage =
    typeof window !== 'undefined'
      ? window.localStorage
      : {
          getItem: () => null,
          setItem: () => null,
          removeItem: () => null,
        };
  return {
    getItem(key: string) {
      try {
        const item = localStorage.getItem(key);
        if (!item) return null;
        return item;
      } catch (error) {
        trackError(error as Error);
        return null;
      }
    },
    setItem(key: string, value: string, tryCount = 0) {
      try {
        const parsed = JSON.parse(value) as { state: TransactionStore; version: number };
        // if we are on the first try, prune transactions
        if (tryCount === 0 && parsed.state.transactions) {
          parsed.state.transactions = pruneTransactions(parsed.state.transactions);
        }
        // if we are on a higher try, remove the oldest transaction
        if (tryCount > 0) {
          let oldestAccount = '';
          let oldestChainId = 0;
          let oldestTimestamp = Date.now();
          for (const account in parsed.state.transactions) {
            for (const chainId in parsed.state.transactions[account]!) {
              const txEpochTime = new Date(
                parsed.state.transactions[account]![chainId]![0]?.timestamp,
              ).getTime();
              if (txEpochTime <= oldestTimestamp) {
                oldestAccount = account;
                oldestChainId = Number(chainId);
                oldestTimestamp = txEpochTime;
              }
            }
          }
          if (oldestAccount !== '' && oldestChainId !== 0) {
            let currentTransactions = parsed.state.transactions[oldestAccount]![oldestChainId]!;
            currentTransactions = currentTransactions.slice(tryCount);

            parsed.state.transactions[oldestAccount]![oldestChainId] = currentTransactions;
            if (Object.keys(parsed.state.transactions[oldestAccount]!).length === 0) {
              delete parsed.state.transactions[oldestAccount];
            }
          }
        }
        // remove all keys with values that are undefined
        for (const [key, value] of Object.entries(parsed.state)) {
          if (value === undefined) {
            delete parsed.state[key as keyof TransactionStore];
          }
        }
        localStorage.setItem(key, JSON.stringify(parsed));
        // send storage event
        if (!isRehydrating) {
          window.dispatchEvent(new Event('transaction-store-updated'));
        }
      } catch (error) {
        logger.error('Error setting item in local storage', error);
        logStorageSizes();
        if (tryCount < 3) {
          this.setItem(key, value, tryCount + 1);
        } else {
          trackError(error as Error);
        }
      }
    },
    removeItem(key: string) {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        trackError(error as Error);
      }
    },
  };
}

export const useTransactionStore = createWithEqualityFn(
  persist<TransactionStore>(
    syncTabs(
      (set, get) => ({
        recentTransaction: false,
        setRecentTransaction: (recentTransaction: boolean) => {
          set({ recentTransaction });
        },
        currentTransaction: null,
        transactions: {},
        addTransaction(tx: Transaction, address: string, chainId: number) {
          const transactions = get().transactions;

          // Check if this transaction already exists (for cross-chain, it might be added twice)
          const existingTx = transactions?.[address]?.[chainId]?.find(
            (t: Transaction) => t.hash === tx.hash,
          );

          // If it already exists, don't add it again or emit duplicate events
          if (existingTx) {
            return;
          }

          const newCurrentTransaction: CurrentTransactionInfo = {
            tx,
            address,
            chainId,
            isSuccessful: false,
            isFailed: false,
          };

          const newTransactions = {
            ...(transactions || {}),
            [address]: {
              ...(transactions?.[address] || {}),
              [chainId]: [...(transactions?.[address]?.[chainId] || []), { ...tx }],
            },
          };

          set({
            currentTransaction: newCurrentTransaction,
            transactions: pruneTransactions(newTransactions),
          });
        },
        updateTransaction(
          txHash: string,
          address: string,
          chainId: number,
          updates: Partial<Transaction>,
        ) {
          const transactions = get().transactions;
          const chainTransactions = transactions?.[address]?.[chainId];

          if (!chainTransactions) return;

          const txIndex = chainTransactions.findIndex((tx) => tx.hash === txHash);
          if (txIndex === -1) return;

          const updatedTransactions = [...chainTransactions];
          updatedTransactions[txIndex] = {
            ...updatedTransactions[txIndex],
            ...updates,
          } as Transaction;

          const newTransactions = {
            ...(transactions || {}),
            [address]: {
              ...(transactions?.[address] || {}),
              [chainId]: updatedTransactions,
            },
          };

          set({
            transactions: pruneTransactions(newTransactions),
          });
        },
        updateCrossChainTransaction(txHash: string, updates: Partial<CrossChainTransaction>) {
          const transactions = get().transactions;
          const newTransactions = cloneDeep(transactions) || {};
          let didUpdate = false;

          for (const account in newTransactions) {
            for (const chainId in newTransactions[account]) {
              const txs = newTransactions[account]?.[Number(chainId)];
              if (!txs) continue;

              newTransactions[account]![Number(chainId)] = txs.map((tx) => {
                if (tx.transactionType !== 'cross-chain' || tx.hash !== txHash) return tx;
                didUpdate = true;
                return { ...tx, ...updates };
              });
            }
          }

          if (!didUpdate) return;

          const currentTransaction = get().currentTransaction;
          const newCurrentTransaction = currentTransaction ? { ...currentTransaction } : null;
          if (
            newCurrentTransaction?.tx.transactionType === 'cross-chain' &&
            newCurrentTransaction.tx.hash === txHash
          ) {
            const { tx } = newCurrentTransaction;
            const updatedTx = { ...tx, ...updates };
            const isSuccessful = isTransactionCompletedSuccessfully(tx, updatedTx);
            const isFailed = isTransactionCompletedWithFailure(tx, updatedTx);

            newCurrentTransaction.tx = updatedTx;
            newCurrentTransaction.isSuccessful = isSuccessful;
            newCurrentTransaction.isFailed = isFailed;
          }

          set({
            currentTransaction: newCurrentTransaction,
            transactions: pruneTransactions(newTransactions),
          });
        },
        completeTransactions(receipts: Transaction[], address: string, chainId: number) {
          const transactions = get().transactions;
          const newTransactions = {
            ...(transactions || {}),
            [address]: {
              ...(transactions?.[address] || {}),
              [chainId]: (transactions?.[address]?.[chainId] || []).map(
                (transaction: Transaction): Transaction => {
                  // Find the receipt that matches the current transaction
                  const receipt = receipts.find(
                    (receipt) =>
                      isTransactionCompletedSuccessfully(transaction, receipt) ||
                      isTransactionCompletedWithFailure(transaction, receipt),
                  );
                  // If no receipt was found, return the transaction as is
                  if (!receipt) return transaction;
                  // Update the transaction with the receipt
                  return {
                    ...transaction,
                    ...receipt,
                    transactionType: transaction.transactionType,
                  } as Transaction;
                },
              ),
            },
          };

          // Update the current transaction if it matches one of the receipts
          const currentTransaction = get().currentTransaction;
          const newCurrentTransaction = currentTransaction ? { ...currentTransaction } : null;
          if (newCurrentTransaction) {
            const successfulReceipt = receipts.find((receipt) =>
              isTransactionCompletedSuccessfully(newCurrentTransaction!.tx, receipt),
            );
            newCurrentTransaction.isSuccessful = !!successfulReceipt;
            const failedReceipt = receipts.find((receipt) =>
              isTransactionCompletedWithFailure(newCurrentTransaction!.tx, receipt),
            );
            newCurrentTransaction.isFailed = !!failedReceipt;
          }

          set({
            currentTransaction: newCurrentTransaction,
            transactions: pruneTransactions(newTransactions),
          });
        },
        claimTerminalAnalytics(hash: string, address: string, chainId: number): boolean {
          const chainTransactions = get().transactions?.[address]?.[chainId];
          if (!chainTransactions) return true;

          const txIndex = chainTransactions.findIndex(
            (tx) => tx.hash === hash || tx.originalHash === hash,
          );
          if (txIndex === -1) return true;
          if (chainTransactions[txIndex]!.analyticsEmitted) return false;

          const transactions = get().transactions;
          const updatedTransactions = [...chainTransactions];
          updatedTransactions[txIndex] = {
            ...updatedTransactions[txIndex],
            analyticsEmitted: true,
          } as Transaction;

          set({
            transactions: pruneTransactions({
              ...(transactions || {}),
              [address]: {
                ...(transactions?.[address] || {}),
                [chainId]: updatedTransactions,
              },
            }),
          });
          return true;
        },
      }),
      {
        channelName: 'matcha-transactions',
        partialize: (state) => ({ transactions: state.transactions }),
      },
    ),
    {
      name: 'matcha-transactions',
      storage: createJSONStorage(customStorage),
      onRehydrateStorage: (state) => {
        isRehydrating = true;
        // keep transactions from the last 30 minutes
        const transactions = state.transactions;

        // Migrate legacy '0x-cross-chain' transaction types to 'cross-chain'
        for (const account in transactions) {
          for (const chainId in transactions[account]) {
            const txs = transactions[account]![Number(chainId)];
            if (!txs) continue;
            for (const tx of txs) {
              if ((tx as { transactionType: string }).transactionType === '0x-cross-chain') {
                (tx as { transactionType: string }).transactionType = 'cross-chain';
              }
              // The gasless lane is retired (intents cutover): its watcher and
              // status endpoint no longer exist, so a still-pending legacy
              // gasless trade can never resolve. Its order expired minutes
              // after submission; stamp the terminal state the relayer would
              // have reported.
              if (tx.transactionType === 'gasless' && isTransactionPending(tx)) {
                tx.raw = {
                  transactions: [],
                  approvalTransactions: [],
                  ...tx.raw,
                  status: 'failed',
                  reason: 'order_expired',
                };
              }
            }
          }
        }

        state.transactions = pruneTransactions(transactions);
        return () => {
          isRehydrating = false;
        };
      },
    },
  ),
  shallow,
);

function injectEventHandler(store: typeof useTransactionStore) {
  if (typeof window === 'undefined') return () => {};
  const storageEventCallback = () => {
    store.persist.rehydrate();
  };
  window.addEventListener('transaction-store-updated', storageEventCallback);

  return () => {
    window.removeEventListener('transaction-store-updated', storageEventCallback);
  };
}

injectEventHandler(useTransactionStore);
// selectors
export const addTransactionSelector = (state: TransactionStore) => state.addTransaction;
export const transactionsSelector = (state: TransactionStore) => state.transactions;
export const completeTransactionsSelector = (state: TransactionStore) => state.completeTransactions;
export const recentTransactionSelector = (state: TransactionStore) => ({
  recentTransaction: state.recentTransaction,
  setRecentTransaction: state.setRecentTransaction,
});
export const updateTransactionSelector = (state: TransactionStore) => state.updateTransaction;
export const claimTerminalAnalyticsSelector = (state: TransactionStore) =>
  state.claimTerminalAnalytics;
export const updateCrossChainTransactionSelector = (state: TransactionStore) =>
  state.updateCrossChainTransaction;
